// src/features/cloche/etat.ts — SCE Issues
// La cloche (03/11) : l'état partagé entre le chargeur (monté une fois,
// ChargeurCloche) et les cloches des en-têtes. Un petit magasin à abonnés : la
// cloche de l'accueil et celle des statistiques montrent la même chose, une
// notification effacée disparaît partout.

import { useSyncExternalStore } from 'react'
import { effacerNotifications, listerMesNotifications } from '@/api/cloche.api'
import { estPerimee, type NotificationAppli } from '@/utils/cloche'

export interface EtatCloche {
  /** Faux tant que rien n'est lu, ou si la liste n'existe pas : pas de cloche. */
  visible: boolean
  notifications: readonly NotificationAppli[]
  /** Bascule de rôle : on lit celles de la personne affichée, on n'efface rien. */
  lectureSeule: boolean
}

let etat: EtatCloche = { visible: false, notifications: [], lectureSeule: false }
let upnCourant = ''
let lectureEnCours: Promise<boolean> | null = null
const abonnes = new Set<() => void>()

function publier(suivant: EtatCloche): void {
  etat = suivant
  for (const f of abonnes) f()
}

/** Qui lit (null : personne — fournisseur, déconnexion). Remet la cloche à zéro au changement. */
export function definirLecteurCloche(upn: string | null, lectureSeule: boolean): void {
  const cle = upn?.trim().toLowerCase() ?? ''
  if (cle === upnCourant && etat.lectureSeule === lectureSeule) return
  upnCourant = cle
  publier({ visible: false, notifications: [], lectureSeule })
}

/** Relit la cloche (et efface ce qui a plus de 30 jours). Vrai si la lecture a abouti. */
export function relireCloche(): Promise<boolean> {
  if (!upnCourant) return Promise.resolve(true)
  if (lectureEnCours) return lectureEnCours
  const upn = upnCourant
  lectureEnCours = (async () => {
    try {
      const lues = await listerMesNotifications(upn)
      if (upn !== upnCourant) return true
      if (lues === null) {
        publier({ ...etat, visible: false, notifications: [] })
        return true
      }
      const maintenant = new Date()
      const perimees = lues.filter((n) => estPerimee(n, maintenant))
      publier({ ...etat, visible: true, notifications: lues.filter((n) => !estPerimee(n, maintenant)) })
      if (perimees.length > 0 && !etat.lectureSeule) {
        void effacerNotifications(perimees.map((n) => n.id)).catch((err: unknown) => console.warn('[cloche] purge :', err))
      }
      return true
    } catch (err) {
      console.warn('[cloche] lecture impossible :', err)
      return false
    } finally {
      lectureEnCours = null
    }
  })()
  return lectureEnCours
}

/** Efface ces notifications : tout de suite à l'écran, puis dans la liste (relue si ça échoue). */
export function effacerDeLaCloche(ids: readonly string[]): void {
  if (ids.length === 0 || etat.lectureSeule) return
  const a = new Set(ids)
  publier({ ...etat, notifications: etat.notifications.filter((n) => !a.has(n.id)) })
  void effacerNotifications(ids).catch((err: unknown) => {
    console.warn('[cloche] effacement impossible :', err)
    void relireCloche()
  })
}

/** Les notifications actuelles, hors React (chargeur). */
export function notificationsDeLaCloche(): readonly NotificationAppli[] {
  return etat.notifications
}

function abonner(f: () => void): () => void {
  abonnes.add(f)
  return () => abonnes.delete(f)
}

export function useCloche(): EtatCloche {
  return useSyncExternalStore(abonner, () => etat)
}
